import type { Coding, Reference } from '@medplum/fhirtypes'
import { ExtendedObservation } from '@seen/core/models/observation'
import {
  groupObservationCaptures,
  type ObservationCapture,
} from '@seen/core/models/observationCaptures'
import {
  OBSERVATION_ANSWER_SET_IDENTIFIER_SYSTEM,
  observationAnswerSetId,
} from '@seen/core/observationReferences'
import { observationCodeFamily } from '@seen/core/terminology/library'
import { useMemo } from 'react'
import { useSearch } from '#src/hooks/useSearch.ts'

// Earlier answers are signed ones: a draft or preliminary answer in a note
// still open is not history yet.
const SIGNED_STATUSES = 'final,amended,corrected'

interface UseObservationHistoryOptions {
  // Leave undefined to skip the search.
  participant: Reference | undefined
  coding: Coding
  // The observation being shown, so it is not listed as its own history.
  exceptReference?: string | null
  limit: number
}

// The participant's earlier observations of this concept, under its own code
// or any standard code mapped to it, newest first.
export function useObservationHistory({
  participant,
  coding,
  exceptReference,
  limit,
}: UseObservationHistoryOptions): ExtendedObservation[] {
  const { system, code } = coding
  const family = useMemo(
    () => observationCodeFamily({ system, code }),
    [system, code]
  )

  const { data } = useSearch(
    ExtendedObservation,
    participant?.reference && code
      ? {
          patient: participant.reference,
          code: family,
          status: SIGNED_STATUSES,
          _sort: '-date',
          // One extra, since the observation being shown may come back too.
          _count: exceptReference ? limit + 1 : limit,
        }
      : undefined,
    { silentErrors: true }
  )

  return useMemo(
    () =>
      (data ?? [])
        .filter(
          (observation) => observation.reference.reference !== exceptReference
        )
        .slice(0, limit),
    [data, exceptReference, limit]
  )
}

interface UseGroupedObservationHistoryOptions {
  participant: Reference | undefined
  coding: Coding
  // Drop the group that contains this observation.
  exceptReference?: string | null
  // Drop every group that contains one of these observations.
  exceptReferences?: string[]
  // Drop this multi-select capture even when the page missed one of its answers.
  exceptGroupId?: string | null
  // How many captures to keep, after answers that share a key are one capture.
  limit: number
}

// The same history as `useObservationHistory`, with each multi-select capture
// kept together. A page of single observations can split a capture, so the
// captures that make the cut are read back whole by their grouping key.
export function useGroupedObservationHistory({
  participant,
  coding,
  exceptReference,
  exceptReferences,
  exceptGroupId,
  limit,
}: UseGroupedObservationHistoryOptions): ObservationCapture[] {
  const { system, code } = coding
  const family = useMemo(
    () => observationCodeFamily({ system, code }),
    [system, code]
  )
  const page = Math.min(100, Math.max(limit * 8, limit))
  const { data } = useSearch(
    ExtendedObservation,
    participant?.reference && code
      ? {
          patient: participant.reference,
          code: family,
          status: SIGNED_STATUSES,
          _sort: '-date',
          _count: page,
        }
      : undefined,
    { silentErrors: true }
  )

  const visible = useMemo(() => {
    const excluded = new Set(exceptReferences ?? [])
    if (exceptReference) {
      excluded.add(exceptReference)
    }
    return groupObservationCaptures(data ?? [])
      .filter((capture) => {
        if (exceptGroupId && capture.key === exceptGroupId) {
          return false
        }
        return !capture.members.some((member) =>
          excluded.has(member.reference.reference ?? '')
        )
      })
      .slice(0, limit)
  }, [data, exceptGroupId, exceptReference, exceptReferences, limit])

  // The captures tied together by an answer-set id, which may have members
  // past the page.
  const groupIds = useMemo(
    () =>
      visible.flatMap((capture) =>
        capture.members[0] &&
        observationAnswerSetId(capture.members[0].identifiers()) === capture.key
          ? [capture.key]
          : []
      ),
    [visible]
  )
  const { data: complete } = useSearch(
    ExtendedObservation,
    participant?.reference && groupIds.length > 0
      ? {
          patient: participant.reference,
          identifier: groupIds
            .map((id) => `${OBSERVATION_ANSWER_SET_IDENTIFIER_SYSTEM}|${id}`)
            .join(','),
          status: SIGNED_STATUSES,
          _count: 100,
        }
      : undefined,
    { silentErrors: true }
  )

  return useMemo(
    () =>
      visible.map((capture) => {
        if (!groupIds.includes(capture.key) || !complete?.length) {
          return capture
        }
        const [whole] = groupObservationCaptures(
          complete.filter(
            (observation) =>
              observationAnswerSetId(observation.identifiers()) === capture.key
          )
        )
        return whole ?? capture
      }),
    [visible, groupIds, complete]
  )
}
